import { useQuery } from '@apollo/client';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useState, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import CategoryFormFields, { CategoryDetectionFields } from './CategoryFormFields.tsx';
import RecommendedCategoriesGrid, { RecommendedCategory } from './RecommendedCategoriesGrid.tsx';
import { SmartInboxProductConfigType, ForwardOption } from './types.ts';
import Button from '../../../core/components/Button.tsx';
import SelectField from '../../../core/components/Fields/SelectField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Modal from '../../../core/components/Modal.tsx';
import { RECOMMENDED_CUSTOM_CATEGORIES } from '../../queries.ts';

export type MailboxOption = { id: string; address: string };

const PlusIcon = withIcon(faPlus);

export type CategoryFormData = {
    name: string;
    detectionCriteria: string;
    supportingKeywords: string[];
    outlookTag: string[];
    moveToOutlookFolder: string;
    tagColor: string;
    markAsRead: boolean;
    enableAutoforwarding: boolean;
    forwardOption: ForwardOption | null;
    forwardingChannel: string;
    forwardingEmailAddresses: string[];
};

export const CATEGORY_FORM_DEFAULT_VALUES: CategoryFormData = {
    name: '',
    detectionCriteria: '',
    supportingKeywords: [],
    outlookTag: [],
    moveToOutlookFolder: '',
    tagColor: 'preset13',
    markAsRead: false,
    enableAutoforwarding: false,
    forwardOption: ForwardOption.EMAIL,
    forwardingChannel: '',
    forwardingEmailAddresses: [],
};

export const OUTLOOK_CATEGORY_COLORS: { name: string; label: string; hex: string }[] = [
    { name: 'preset0', label: 'Red', hex: '#F7C6C7' },
    { name: 'preset1', label: 'Orange', hex: '#FFDDB3' },
    { name: 'preset2', label: 'Brown', hex: '#F1D9CC' },
    { name: 'preset3', label: 'Yellow', hex: '#FCEFD3' },
    { name: 'preset4', label: 'Green', hex: '#CEF0CD' },
    { name: 'preset5', label: 'Teal', hex: '#A6E9ED' },
    { name: 'preset6', label: 'Olive', hex: '#E5F1D3' },
    { name: 'preset7', label: 'Blue', hex: '#D4F4FD' },
    { name: 'preset8', label: 'Purple', hex: '#E7E4FB' },
    { name: 'preset9', label: 'Cranberry', hex: '#F6D1D5' },
    { name: 'preset10', label: 'Steel', hex: '#94C8D4' },
    { name: 'preset11', label: 'DarkSteel', hex: '#EAE8E8' },
    { name: 'preset12', label: 'Gray', hex: '#D8DFE0' },
    { name: 'preset13', label: 'DarkGray', hex: '#CECCCB' },
    { name: 'preset14', label: 'Black', hex: '#C4C4C4' },
    { name: 'preset15', label: 'DarkRed', hex: '#E9C7CD' },
    { name: 'preset16', label: 'DarkOrange', hex: '#F9DCD1' },
    { name: 'preset17', label: 'DarkBrown', hex: '#CAADA3' },
    { name: 'preset18', label: 'DarkYellow', hex: '#F5EECE' },
    { name: 'preset19', label: 'DarkGreen', hex: '#9AD29A' },
    { name: 'preset20', label: 'DarkTeal', hex: '#92D1D1' },
    { name: 'preset21', label: 'DarkOlive', hex: '#BDD99B' },
    { name: 'preset22', label: 'DarkBlue', hex: '#C2D6E7' },
    { name: 'preset23', label: 'DarkPurple', hex: '#B9A3D3' },
    { name: 'preset24', label: 'DarkCranberry', hex: '#D4A0B0' },
];

type Props = {
    form: UseFormReturn<CategoryFormData>;
    destinationFolders: Array<{ id: string; name: string }>;
    isLoading: boolean;
    integrationId: string;
    channels: Record<string, any>;
    onClose: () => void;
    onSubmit: (data: CategoryFormData) => Promise<void>;
    onCreateRecommended: (categories: RecommendedCategory[]) => Promise<void>;
    title: string;
    submitButtonLabel: string;
    skipRecommendations?: boolean;
    // mailboxes-only: collapses the create flow to just Name + Detection Criteria (no keyword/
    // Outlook/automation fields) — those are edited later on the category detail page instead
    quickCreate?: boolean;
    mailboxOptions?: MailboxOption[];
    onMailboxChange?: (id: string) => void;
};

const CreateCustomCategoryModal = ({
    form,
    destinationFolders,
    isLoading,
    integrationId,
    channels,
    onClose,
    onSubmit,
    onCreateRecommended,
    title,
    submitButtonLabel,
    skipRecommendations = false,
    quickCreate = false,
    mailboxOptions,
    onMailboxChange,
}: Props) => {
    const { t } = useTranslation('integrations');
    const [view, setView] = useState<'select' | 'custom'>(skipRecommendations ? 'custom' : 'select');
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const { isSubmitting } = form.formState;

    const mailboxReady = !mailboxOptions || !!integrationId;

    const { data: recommendedData, loading: recommendedLoading } = useQuery(RECOMMENDED_CUSTOM_CATEGORIES, {
        variables: { integrationId },
        fetchPolicy: 'network-only',
        skip: skipRecommendations || !integrationId,
    });

    const recommendations: RecommendedCategory[] = recommendedData?.recommendedCustomCategories || [];

    useEffect(() => {
        // quickCreate has a single unified view — no select/custom toggle to manage
        if (quickCreate) return;
        if (mailboxOptions && !integrationId) return;
        if (skipRecommendations) return;
        setView(recommendedLoading || recommendations.length > 0 ? 'select' : 'custom');
    }, [quickCreate, mailboxOptions, integrationId, skipRecommendations, recommendedLoading, recommendations.length]);

    const toggleSelect = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleAddRecommended = async () => {
        const selected = recommendations.filter((r) => selectedIds.has(r.id));
        await onCreateRecommended(selected);
    };

    const selectButtons = (
        <>
            <Button type="button" onClick={onClose} disabled={isLoading}>
                {t('smartInbox.form.cancelButton')}
            </Button>
            <Button
                variant="primary"
                type="button"
                disabled={selectedIds.size === 0 || isLoading}
                loading={isLoading}
                onClick={handleAddRecommended}
            >
                {t('smartInbox.config.events.customCategories.form.addButton')}
            </Button>
        </>
    );

    // one primary action at a time: "Add" for a recommendation selection, "Create" for the inline name/criteria fields
    const quickCreateButtons = (
        <>
            <Button type="button" onClick={onClose} disabled={isLoading || isSubmitting}>
                {t('smartInbox.form.cancelButton')}
            </Button>
            {selectedIds.size > 0 ? (
                <Button
                    variant="primary"
                    type="button"
                    disabled={isLoading}
                    loading={isLoading}
                    onClick={handleAddRecommended}
                >
                    {t('smartInbox.config.events.customCategories.form.addButton')}
                </Button>
            ) : (
                <Button
                    variant="primary"
                    type="submit"
                    form="create-custom-category-modal-form"
                    disabled={isSubmitting}
                    loading={isSubmitting}
                >
                    {submitButtonLabel}
                </Button>
            )}
        </>
    );

    const customButtons = (
        <>
            <Button
                type="button"
                onClick={recommendations.length > 0 ? () => setView('select') : onClose}
                disabled={isSubmitting}
            >
                {t('smartInbox.form.cancelButton')}
            </Button>
            <Button
                variant="primary"
                type="submit"
                form="create-custom-category-modal-form"
                disabled={isSubmitting}
                loading={isSubmitting}
            >
                {submitButtonLabel}
            </Button>
        </>
    );

    const pickerButtons = (
        <Button type="button" onClick={onClose}>
            {t('smartInbox.form.cancelButton')}
        </Button>
    );

    return (
        <Modal
            visible
            onClose={onClose}
            className="w-[50rem]!"
            title={title}
            buttons={
                !mailboxReady
                    ? pickerButtons
                    : quickCreate
                      ? quickCreateButtons
                      : view === 'select'
                        ? selectButtons
                        : customButtons
            }
        >
            <div className="flex flex-col gap-5">
                {mailboxOptions && (
                    <div className="flex flex-col gap-2">
                        <div>
                            <p className="text-sm font-semibold text-content-strong">
                                {t('detail.addCategoryPage.mailboxTitle', { ns: 'mailboxes' })}
                            </p>
                            <p className="text-sm text-content-medium">
                                {t('detail.addCategoryPage.mailboxDescription', { ns: 'mailboxes' })}
                            </p>
                        </div>
                        <FormField label={t('detail.addCategoryPage.mailboxLabel', { ns: 'mailboxes' })} required>
                            <SelectField
                                value={integrationId || undefined}
                                options={mailboxOptions.map((m) => ({ value: m.id, label: m.address }))}
                                renderOption={(option) => option.label}
                                placeholder={t('detail.addCategoryPage.mailboxPlaceholder', { ns: 'mailboxes' })}
                                onValueChange={onMailboxChange}
                            />
                        </FormField>
                    </div>
                )}

                {mailboxReady &&
                    (quickCreate ? (
                        <div className="flex flex-col gap-5">
                            <p className="text-sm text-content-medium">
                                {t('smartInbox.config.events.customCategories.form.selectDescription')}
                            </p>

                            {recommendations.length > 0 && (
                                <div className="flex flex-col gap-3">
                                    <div className="mb-2">
                                        <p className="text-sm font-semibold text-content-strong">
                                            {t('smartInbox.config.events.customCategories.form.recommendedTitle')}
                                        </p>
                                        <p className="text-sm text-content-medium">
                                            {t('smartInbox.config.events.customCategories.form.recommendedDescription')}
                                        </p>
                                    </div>

                                    <RecommendedCategoriesGrid
                                        recommendations={recommendations}
                                        loading={recommendedLoading}
                                        selectedIds={selectedIds}
                                        onToggle={toggleSelect}
                                        assistantBadge={quickCreate}
                                    />
                                </div>
                            )}

                            {selectedIds.size === 0 && (
                                <div className="flex flex-col gap-3">
                                    <div>
                                        <p className="text-sm font-semibold text-content-strong">
                                            {t('smartInbox.config.events.customCategories.form.customTitle')}
                                        </p>
                                        <p className="text-sm text-content-medium">
                                            {t('smartInbox.config.events.customCategories.form.customDescription')}
                                        </p>
                                    </div>
                                    <form
                                        className="flex flex-col gap-4"
                                        id="create-custom-category-modal-form"
                                        onSubmit={form.handleSubmit(onSubmit)}
                                    >
                                        <CategoryDetectionFields
                                            form={form}
                                            isLoading={isLoading}
                                            showHeader={false}
                                            showKeywords={false}
                                        />
                                    </form>
                                </div>
                            )}
                        </div>
                    ) : view === 'select' ? (
                        <div className="flex flex-col gap-5">
                            <p className="text-sm text-content-medium">
                                {t('smartInbox.config.events.customCategories.form.selectDescription')}
                            </p>

                            {recommendations.length > 0 && (
                                <div className="flex flex-col gap-3">
                                    <div className="mb-2">
                                        <p className="text-sm font-semibold text-content-strong">
                                            {t('smartInbox.config.events.customCategories.form.recommendedTitle')}
                                        </p>
                                        <p className="text-sm text-content-medium">
                                            {t('smartInbox.config.events.customCategories.form.recommendedDescription')}
                                        </p>
                                    </div>

                                    <RecommendedCategoriesGrid
                                        recommendations={recommendations}
                                        loading={recommendedLoading}
                                        selectedIds={selectedIds}
                                        onToggle={toggleSelect}
                                        assistantBadge={quickCreate}
                                    />
                                </div>
                            )}

                            {selectedIds.size === 0 && (
                                <div className="flex flex-col gap-3">
                                    <p className="text-sm font-semibold text-content-strong mb-2">
                                        {t('smartInbox.config.events.customCategories.form.customTitle')}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setView('custom')}
                                        className="flex items-center px-3 py-1.5 cursor-pointer rounded border border-line-weak rounded-sm gap-2 text-sm text-content-default font-medium w-fit shadow-sm"
                                    >
                                        <PlusIcon className="mr-2" />
                                        {t('smartInbox.config.events.customCategories.createCustom')}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <form
                            className="flex flex-col gap-6"
                            id="create-custom-category-modal-form"
                            onSubmit={form.handleSubmit(onSubmit)}
                        >
                            <CategoryFormFields
                                channels={channels}
                                form={form}
                                destinationFolders={destinationFolders}
                                isLoading={isLoading}
                            />
                        </form>
                    ))}
            </div>
        </Modal>
    );
};

export default CreateCustomCategoryModal;
